/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { Pencil, Plus } from 'lucide-react';
import { useCallback, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '../../components/ui/badge.js';
import { Button } from '../../components/ui/button.js';
import { Modal } from '../../components/ui/modal.js';
import { QueryState } from '../../components/ui/query-state.js';
import { type Role } from '../../lib/api/roles.js';
import { useRoles } from '../../lib/queries/roles.js';

import { RoleEditor } from './role-editor.js';

const LEVEL_TONE = ['slate', 'violet', 'amber', 'green'] as const;

function RoleCard({
  role,
  onEdit,
}: {
  readonly role: Role;
  readonly onEdit: (role: Role) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const active = role.modules.filter((m) => m.accessLevel > 0);
  const edit = useCallback(() => {
    onEdit(role);
  }, [onEdit, role]);
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-700 dark:bg-slate-900">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-2">
          <h3 className="font-semibold text-slate-800 dark:text-white">{role.name}</h3>
          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-500 dark:bg-slate-800 dark:text-slate-400">
            {t('rbac.userCount', { count: role.userCount })}
          </span>
          {role.isSystem && <Badge label={t('rbac.system')} tone="slate" />}
        </div>
        <Button variant="outline" size="sm" onClick={edit}>
          <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
          {t('common.edit')}
        </Button>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        {active.length === 0 ? (
          <span className="text-sm text-slate-400">{t('rbac.noModules')}</span>
        ) : (
          active.map((m) => (
            <span
              key={m.module}
              className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${
                LEVEL_TONE[m.accessLevel] === 'green'
                  ? 'bg-emerald-50 text-emerald-700'
                  : LEVEL_TONE[m.accessLevel] === 'amber'
                    ? 'bg-amber-50 text-amber-700'
                    : 'bg-[var(--accent)]/10 text-[var(--accent-strong)]'
              }`}
            >
              {t(`rbac.module.${m.module}`)} · {t(`rbac.level.${String(m.accessLevel)}`)}
            </span>
          ))
        )}
      </div>
    </div>
  );
}

/** Super Admin → Roles & Permissions: role cards + create/edit module access. */
export function RolesPanel(): JSX.Element {
  const { t } = useTranslation();
  const roles = useRoles(true);
  const [editing, setEditing] = useState<Role | null>(null);
  const [creating, setCreating] = useState(false);

  const list = roles.data ?? [];
  const openCreate = useCallback(() => {
    setCreating(true);
  }, []);
  const openEdit = useCallback((role: Role) => {
    setEditing(role);
  }, []);
  const close = useCallback(() => {
    setEditing(null);
    setCreating(false);
  }, []);
  const onModalChange = useCallback(
    (open: boolean) => {
      if (!open) {
        close();
      }
    },
    [close],
  );

  return (
    <div>
      <div className="mb-4 flex justify-end">
        <Button onClick={openCreate}>
          <Plus className="h-4 w-4" aria-hidden="true" />
          {t('rbac.createRole')}
        </Button>
      </div>
      <QueryState
        isLoading={roles.isLoading}
        isError={roles.isError}
        isEmpty={list.length === 0}
        loadingLabel={t('common.loading')}
        errorLabel={t('common.loadError')}
        emptyLabel={t('rbac.empty')}
      >
        <div className="space-y-3">
          {list.map((role) => (
            <RoleCard key={role.id} role={role} onEdit={openEdit} />
          ))}
        </div>
      </QueryState>

      <Modal
        open={creating || editing !== null}
        onOpenChange={onModalChange}
        title={editing !== null ? editing.name : t('rbac.createRole')}
        description={t('rbac.editorSubtitle')}
        closeLabel={t('common.cancel')}
      >
        <RoleEditor role={editing} onDone={close} />
      </Modal>
    </div>
  );
}
